import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const QuickApplyDialog = ({ job, open, onOpenChange }) => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const isMember = user?.role === "member";
  const [step, setStep] = useState(1); // 1 name+mobile, 2 otp, 3 success
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [appCode, setAppCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1);
    setOtp("");
    setDevOtp("");
    setBusy(false);
  };

  const applyAsMember = async () => {
    setBusy(true);
    try {
      const source = localStorage.getItem("ut_source") || undefined;
      const res = await api.post("/applications/apply", { job_id: job.id, source });
      setAppCode(res.data.application.code);
      setStep(3);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const requestOtp = async () => {
    if (name.trim().length < 2) return toast.error("Please enter your full name");
    if (!/^[0-9]{10}$/.test(mobile)) return toast.error("Please enter a valid 10-digit mobile number");
    setBusy(true);
    try {
      const res = await api.post("/auth/request-otp", { mobile });
      setDevOtp(res.data.dev_otp);
      setStep(2);
      toast.success("OTP sent to your mobile number");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitApplication = async () => {
    if (otp.length !== 6) return toast.error("Please enter the 6-digit OTP");
    setBusy(true);
    try {
      const source = localStorage.getItem("ut_source") || undefined;
      const res = await api.post("/applications/apply", { job_id: job.id, name: name.trim(), mobile, otp, source });
      login(res.data.token, res.data.user);
      setAppCode(res.data.application.code);
      setStep(3);
    } catch (err) {
      toast.error(getErrorMessage(err));
      if (err.response?.status === 400) setOtp("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md rounded-xl" data-testid="quick-apply-dialog">
        {step === 3 ? (
          <div className="text-center py-4" data-testid="apply-success-screen">
            <span className="inline-flex h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h2 className="font-heading font-semibold text-xl">Application Submitted!</h2>
            <p className="text-sm text-muted-foreground mt-1.5">
              You applied for <span className="font-medium text-foreground">{job?.title}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Application ID: <span className="font-mono font-semibold text-foreground">{appCode}</span>
            </p>
            <p className="text-xs text-muted-foreground mt-3">Our recruitment team will contact you soon. Track your application anytime.</p>
            <div className="grid grid-cols-1 gap-2.5 mt-6">
              <Button className="rounded-lg w-full" data-testid="apply-success-track-button" onClick={() => { onOpenChange(false); reset(); navigate("/member"); }}>
                Track My Application
              </Button>
              <Button variant="outline" className="rounded-lg w-full bg-white" data-testid="apply-success-browse-button" onClick={() => { onOpenChange(false); reset(); navigate("/jobs"); }}>
                Continue Browsing Jobs
              </Button>
            </div>
          </div>
        ) : isMember ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">Apply for {job?.title}</DialogTitle>
              <DialogDescription>
                You're logged in — apply with one click. No OTP needed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <div className="rounded-lg border border-border bg-muted/40 px-4 py-3" data-testid="member-apply-identity">
                <p className="text-sm font-medium">{user.name}</p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">{user.mobile}</p>
              </div>
              <Button onClick={applyAsMember} disabled={busy} className="w-full rounded-lg" data-testid="member-apply-submit-button">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit Application
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">Apply for {job?.title}</DialogTitle>
              <DialogDescription>
                {step === 1 ? "No resume needed — just your name and mobile number." : "Enter the OTP to submit your application."}
              </DialogDescription>
            </DialogHeader>

            {step === 1 ? (
              <div className="space-y-4 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="apply-name">Full name</Label>
                  <Input id="apply-name" data-testid="quick-apply-name-input" placeholder="e.g. Priya Sharma" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="apply-mobile">Mobile number</Label>
                  <Input
                    id="apply-mobile"
                    data-testid="quick-apply-mobile-input"
                    placeholder="10-digit mobile number"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <Button onClick={requestOtp} disabled={busy} className="w-full rounded-lg" data-testid="quick-apply-send-otp-button">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send OTP
                </Button>
              </div>
            ) : (
              <div className="space-y-4 pt-1">
                <div className="flex flex-col items-center gap-3">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="quick-apply-otp-input">
                    <InputOTPGroup>
                      {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                    </InputOTPGroup>
                  </InputOTP>
                  {devOtp && (
                    <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5" data-testid="dev-otp-chip">
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="text-xs text-emerald-700">Dev mode OTP:</span>
                      <span className="font-mono text-sm font-semibold text-emerald-800">{devOtp}</span>
                    </div>
                  )}
                </div>
                <Button onClick={submitApplication} disabled={busy} className="w-full rounded-lg" data-testid="quick-apply-submit-button">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify & Submit Application
                </Button>
                <Button variant="ghost" onClick={() => setStep(1)} className="w-full rounded-lg" data-testid="quick-apply-back-button">
                  Back
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
